import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosRequestConfig } from 'axios';
import * as Sentry from '@sentry/react';
import { API_BASE_URL, CHAT_API_BASE_URL, AP_API_BASE_URL, KB_API_BASE_URL, API_TIMEOUT, getAuthTokens, clearAuthTokens } from './config';
import { refreshAuthToken } from '../auth/auth.api';
import { getWorkflowToken } from '../index';
import { VENDOR_REGEX } from '../../utils/keywords';

// Extend AxiosRequestConfig to include _retry flag
interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
    _retry?: boolean;
}

// Custom error type for generic errors
interface CustomError extends Error {
    response?: {
        status?: number;
        statusText: string;
        data: {
            error: string;
            message: string;
        };
    };
    config?: {
        url: string;
        method?: string;
    };
}

// Type for recursive data structures that might need processing
type RecursiveData =
    | string
    | number
    | boolean
    | null
    | undefined
    | RecursiveDataArray
    | RecursiveDataObject;

interface RecursiveDataObject {
    [key: string]: RecursiveData;
}

type RecursiveDataArray = RecursiveData[];

// Function to replace vendor keyword with "voicebot" in any data structure
function replaceVendorWithVoicebot(data: RecursiveData): RecursiveData {
    if (data === null || data === undefined) {
        return data;
    }

    if (typeof data === 'string') {
        // Replace vendor keyword with "voicebot" (case insensitive)
        return data.replace(VENDOR_REGEX, 'voicebot');
    }

    if (Array.isArray(data)) {
        return data.map(item => replaceVendorWithVoicebot(item));
    }

    if (typeof data === 'object') {
        const result: RecursiveDataObject = {};
        for (const key in data) {
            // Replace in both keys and values
            const newKey = key.replace(VENDOR_REGEX, 'voicebot');
            result[newKey] = replaceVendorWithVoicebot(data[key]);
        }
        return result;
    }

    return data;
}

// Create base axios instance
export const apiClient: AxiosInstance = axios.create({
    baseURL: API_BASE_URL,
    timeout: API_TIMEOUT,
    headers: {
        'Accept': 'application/json',
        'ngrok-skip-browser-warning': true
    }
});

// Create authenticated client for protected routes
export const authenticatedClient: AxiosInstance = axios.create({
    baseURL: API_BASE_URL,
    timeout: API_TIMEOUT,
    headers: {
        'Accept': 'application/json',
        'ngrok-skip-browser-warning': true
    }
});

// Create authenticated client specifically for analytics API
// Analytics queries (dashboard graphs) can be heavy, so allow a longer 120s timeout
const ANALYTICS_API_TIMEOUT = 120000; // 120 seconds
export const analyticsClient: AxiosInstance = axios.create({
    baseURL: import.meta.env.VITE_ANALYTICS_API_BASE_URL,
    timeout: ANALYTICS_API_TIMEOUT,
    headers: {
        'Accept': 'application/json',
        'ngrok-skip-browser-warning': true
    }
});

// Create client specifically for billing API
export const billingClient: AxiosInstance = axios.create({
    baseURL: import.meta.env.VITE_BILLING_API_URL,
    timeout: 0, // No timeout
    headers: {
        'Accept': '*/*', // Accept any content type, not just JSON
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': true
    }
});

// Create authenticated client specifically for chat/messaging API
export const chatClient: AxiosInstance = axios.create({
    baseURL: CHAT_API_BASE_URL,
    timeout: API_TIMEOUT,
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
    }
});

// Create authenticated client specifically for Activepieces MCP API
export const mcpClient: AxiosInstance = axios.create({
    baseURL: AP_API_BASE_URL,
    timeout: API_TIMEOUT,
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
    }
});

// Create authenticated client specifically for Knowledge Base API
export const knowledgeBaseClient: AxiosInstance = axios.create({
    baseURL: KB_API_BASE_URL,
    timeout: API_TIMEOUT,
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': true
    }
});

let isRefreshing = false;
let failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: Error | unknown) => void;
}> = [];

const processQueue = (error: Error | unknown, token: string | null = null) => {
    failedQueue.forEach(promise => {
        if (error) {
            promise.reject(error);
        } else {
            promise.resolve(token!);
        }
    });
    failedQueue = [];
};

// Listen for token changes in other tabs
window.addEventListener('storage', (event) => {
    if (event.key === 'auth_timestamp') {
        const tokens = getAuthTokens();
        if (!tokens) {
            // Tokens were cleared in another tab - clear MCP cache too
            clearMcpAuthCache();
            window.location.href = '/auth/login';
        } else {
            // Tokens changed (possibly org switch) - clear MCP cache
            clearMcpAuthCache();
        }
    }
});

// Add auth token to authenticated requests
authenticatedClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const tokens = getAuthTokens();

        if (tokens?.access_token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${tokens.access_token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add response interceptor to handle token refresh
authenticatedClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as ExtendedAxiosRequestConfig;

        if (!originalRequest) {
            return Promise.reject(error);
        }

        // If the error is 401 and we haven't tried to refresh the token yet
        if (error.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                try {
                    // If we're already refreshing, add this request to the queue
                    const token = await new Promise<string>((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    });

                    originalRequest.headers = originalRequest.headers || {};
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return axios(originalRequest);
                } catch (err) {
                    clearAuthTokens();
                    window.location.href = '/auth/login';
                    return Promise.reject(err);
                }
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const response = await refreshAuthToken();

                if (response.error || !response.data) {
                    throw new Error(response.error || 'Failed to refresh token');
                }

                const { access_token } = response.data;
                processQueue(null, access_token);

                // Retry the original request with new token
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers.Authorization = `Bearer ${access_token}`;
                return axios(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                clearAuthTokens();
                window.location.href = '/auth/login';
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

// Apply the same authentication and refresh token logic to analytics client
analyticsClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const tokens = getAuthTokens();

        if (tokens?.access_token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${tokens.access_token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

analyticsClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as ExtendedAxiosRequestConfig;

        if (!originalRequest) {
            return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                try {
                    const token = await new Promise<string>((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    });

                    originalRequest.headers = originalRequest.headers || {};
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return axios(originalRequest);
                } catch (err) {
                    clearAuthTokens();
                    window.location.href = '/auth/login';
                    return Promise.reject(err);
                }
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const response = await refreshAuthToken();

                if (response.error || !response.data) {
                    throw new Error(response.error || 'Failed to refresh token');
                }

                const { access_token } = response.data;
                processQueue(null, access_token);

                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers.Authorization = `Bearer ${access_token}`;
                return axios(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                clearAuthTokens();
                window.location.href = '/auth/login';
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

// Add the same authentication and refresh token logic to billing client
billingClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const tokens = getAuthTokens();

        if (tokens?.access_token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${tokens.access_token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

billingClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as ExtendedAxiosRequestConfig;

        if (!originalRequest) {
            return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                try {
                    const token = await new Promise<string>((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    });

                    originalRequest.headers = originalRequest.headers || {};
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return axios(originalRequest);
                } catch (err) {
                    clearAuthTokens();
                    window.location.href = '/auth/login';
                    return Promise.reject(err);
                }
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const response = await refreshAuthToken();

                if (response.error || !response.data) {
                    throw new Error(response.error || 'Failed to refresh token');
                }

                const { access_token } = response.data;
                processQueue(null, access_token);

                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers.Authorization = `Bearer ${access_token}`;
                return axios(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                clearAuthTokens();
                window.location.href = '/auth/login';
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

// Add request interceptor for chatClient to include authentication token
chatClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const tokens = getAuthTokens();
        if (tokens?.access_token) {
            config.headers.Authorization = `Bearer ${tokens.access_token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add response interceptor for chatClient to handle token refresh
chatClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as ExtendedAxiosRequestConfig;

        if (!originalRequest) {
            return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                try {
                    const token = await new Promise<string>((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    });

                    originalRequest.headers = originalRequest.headers || {};
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return axios(originalRequest);
                } catch (err) {
                    clearAuthTokens();
                    window.location.href = '/auth/login';
                    return Promise.reject(err);
                }
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const response = await refreshAuthToken();

                if (response.error || !response.data) {
                    throw new Error(response.error || 'Failed to refresh token');
                }

                const { access_token } = response.data;
                processQueue(null, access_token);

                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers.Authorization = `Bearer ${access_token}`;
                return axios(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                clearAuthTokens();
                window.location.href = '/auth/login';
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

// Add request interceptor for knowledgeBaseClient to include authentication token
knowledgeBaseClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const tokens = getAuthTokens();
        if (tokens?.access_token) {
            config.headers.Authorization = `Bearer ${tokens.access_token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add response interceptor for knowledgeBaseClient to handle token refresh
knowledgeBaseClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as ExtendedAxiosRequestConfig;

        if (!originalRequest) {
            return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                try {
                    const token = await new Promise<string>((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    });

                    originalRequest.headers = originalRequest.headers || {};
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return axios(originalRequest);
                } catch (err) {
                    clearAuthTokens();
                    window.location.href = '/auth/login';
                    return Promise.reject(err);
                }
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const response = await refreshAuthToken();

                if (response.error || !response.data) {
                    throw new Error(response.error || 'Failed to refresh token');
                }

                const { access_token } = response.data;
                processQueue(null, access_token);

                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers.Authorization = `Bearer ${access_token}`;
                return axios(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                clearAuthTokens();
                window.location.href = '/auth/login';
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

// MCP authentication types and cache
interface ActivepiecesAuthResponse {
    id: string;
    projectId: string;
    token: string;
    platformId: string;
    firstName: string;
    lastName: string;
    email: string;
}

let mcpAuthCache: { data: ActivepiecesAuthResponse; timestamp: number; userId?: string; orgId?: string } | null = null;
const MCP_AUTH_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Export function to clear MCP auth cache (called on logout or user/org change)
export const clearMcpAuthCache = () => {
    console.log('[MCP Client] Clearing MCP auth cache');
    mcpAuthCache = null;
};

// Export function to get the current project ID from MCP auth
// This triggers the auth flow if needed and returns the cached projectId
export const getMcpProjectId = async (): Promise<string | null> => {
    try {
        const auth = await getMcpAuth();
        return auth.projectId || null;
    } catch (error) {
        console.error('[MCP Client] Error getting project ID:', error);
        return null;
    }
};

// Helper function to get Activepieces authentication
const getMcpAuth = async (): Promise<ActivepiecesAuthResponse> => {
    // Get current user info to check for user/org changes
    const tokens = getAuthTokens();
    if (!tokens) {
        // No auth tokens, clear cache and throw
        clearMcpAuthCache();
        throw new Error('No authentication tokens available');
    }
    
    // Check cache first
    const now = Date.now();
    if (mcpAuthCache && (now - mcpAuthCache.timestamp) < MCP_AUTH_CACHE_DURATION) {
        return mcpAuthCache.data;
    }

    // Get workflow token and exchange for Activepieces auth
    const workflowTokenResp = await getWorkflowToken();
    const externalToken = workflowTokenResp.data;
    
    const response = await fetch(`${AP_API_BASE_URL}/managed-authn/external-token`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            externalAccessToken: externalToken
        })
    });
    
    if (!response.ok) {
        throw new Error(`MCP Authentication failed: ${response.status}`);
    }
    
    const authData = await response.json();
    
    // Cache the result with current user/org context
    mcpAuthCache = { 
        data: authData, 
        timestamp: now,
        userId: authData.id,
        orgId: authData.platformId
    };
    
    return authData;
};

// Add MCP authentication interceptor
mcpClient.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
        try {
            const auth = await getMcpAuth();
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${auth.token}`;
            
            // Don't set Content-Type for rotation endpoints (empty body)
            if (config.url?.includes('/rotate') && config.method?.toLowerCase() === 'post') {
                delete config.headers['Content-Type'];
            }
            
            // Add projectId as query param if not already present
            if (config.url && !config.url.includes('projectId=') && !config.params?.projectId) {
                config.params = { ...config.params, projectId: auth.projectId };
            }
            
            // For POST requests to /mcp-servers (create), also add projectId to request body
            if (config.method?.toLowerCase() === 'post' && 
                config.url === '/mcp-servers' && 
                config.data && 
                typeof config.data === 'object' && 
                !config.data.projectId) {
                config.data = { ...config.data, projectId: auth.projectId };
            }
            
            return config;
        } catch (error) {
            console.error('[MCP Client] Authentication failed:', error);
            return Promise.reject(error);
        }
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add MCP response interceptor for error handling
mcpClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        if (error.response?.status === 401) {
            // Clear MCP auth cache on 401
            clearMcpAuthCache();
            console.log('[MCP Client] 401 received, cleared auth cache');
        }
        return Promise.reject(error);
    }
);

// Generic error messages for users
const getGenericErrorMessage = (status?: number): string => {
    switch (status) {
        case 400:
            return 'Invalid request. Please check your input and try again.';
        case 401:
            return 'Authentication required. Please log in and try again.';
        case 403:
            return 'Access denied. You do not have permission to perform this action.';
        case 404:
            return 'The requested resource was not found.';
        case 409:
            return 'A conflict occurred. The resource may already exist.';
        case 422:
            return 'The provided data is invalid. Please check your input.';
        case 429:
            return 'Too many requests. Please wait a moment and try again.';
        case 500:
            return 'An internal server error occurred. Please try again later.';
        case 502:
            return 'Service temporarily unavailable. Please try again later.';
        case 503:
            return 'Service is currently under maintenance. Please try again later.';
        case 504:
            return 'Request timeout. Please check your connection and try again.';
        default:
            return 'An unexpected error occurred. Please try again later.';
    }
};

// Enhanced response interceptor that shows generic errors
const addResponseFilter = (client: AxiosInstance) => {
    client.interceptors.response.use(
        response => {
            // Skip processing for binary data responses (like Excel files or PDFs)
            const contentType = response.headers['content-type'] || '';
            if (contentType.includes('application/vnd.openxmlformats') ||
                contentType.includes('application/zip') ||
                contentType.includes('application/pdf') ||
                contentType.includes('application/octet-stream') ||
                response.config.responseType === 'arraybuffer' ||
                response.config.responseType === 'blob') {
                return response;
            }

            // Apply the filter to response data if it exists (for success responses)
            if (response.data) {
                response.data = replaceVendorWithVoicebot(response.data) as typeof response.data;
            }
            return response;
        },
        error => {
            // Replace error with generic user-friendly message
            const status = error.response?.status;
            const genericMessage = getGenericErrorMessage(status);

            // Create a clean error object with generic message
            const cleanError = new Error(genericMessage) as CustomError;
            cleanError.response = {
                status: status,
                statusText: genericMessage,
                data: {
                    error: genericMessage,
                    message: genericMessage
                }
            };
            cleanError.config = {
                url: '/api/request', // Generic URL
                method: error.config?.method
            };

            return Promise.reject(cleanError);
        }
    );
};

// Error logging interceptor with generic messages and Sentry integration
const addErrorLogging = (client: AxiosInstance, clientName: string = 'unknown') => {
    client.interceptors.response.use(
        response => response,
        error => {
            if (error.response?.status !== 401) {  // Don't log 401s as they're handled by refresh
                // Log with generic information only
                console.error('API Error:', {
                    url: '/api/request', // Generic URL
                    method: error.config?.method,
                    status: error.response?.status,
                    message: getGenericErrorMessage(error.response?.status),
                    timestamp: new Date().toISOString()
                });
                
                // Send to Sentry with detailed context (but sanitized URL)
                Sentry.captureException(error, {
                    contexts: {
                        api: {
                            client: clientName,
                            method: error.config?.method?.toUpperCase(),
                            status: error.response?.status,
                            status_text: error.response?.statusText,
                            base_url: error.config?.baseURL,
                            // Keep actual URL for Sentry (helps debugging)
                            url: error.config?.url,
                            timeout: error.config?.timeout,
                        },
                        response: {
                            has_data: !!error.response?.data,
                            content_type: error.response?.headers?.['content-type'],
                        }
                    },
                    tags: {
                        'api.client': clientName,
                        'api.status': error.response?.status?.toString() || 'unknown',
                        'api.method': error.config?.method?.toUpperCase() || 'unknown',
                    },
                    level: error.response?.status >= 500 ? 'error' : 'warning',
                    fingerprint: [
                        clientName,
                        error.config?.method || 'unknown',
                        error.response?.status?.toString() || 'unknown',
                    ],
                });
            }
            return Promise.reject(error);
        }
    );
};

addErrorLogging(apiClient, 'main');
addErrorLogging(authenticatedClient, 'authenticated');
addErrorLogging(analyticsClient, 'analytics');
addErrorLogging(billingClient, 'billing');
addErrorLogging(chatClient, 'chat');
addErrorLogging(mcpClient, 'mcp');
addErrorLogging(knowledgeBaseClient, 'knowledge-base');

// Apply the response filter to all clients
addResponseFilter(apiClient);
addResponseFilter(authenticatedClient);
addResponseFilter(analyticsClient);
addResponseFilter(billingClient);
addResponseFilter(mcpClient);
addResponseFilter(knowledgeBaseClient);
